import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t, languageNames, availableLanguages } = useLanguage();
  const langMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLangMenuOpen(false);
  }, [location]);

  // Close language menu when clicking outside
  useEffect(() => {
    if (!isLangMenuOpen) return;
    
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    // Small delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLangMenuOpen]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/experiences', label: t('nav.experiences') },
    { path: '/cities', label: t('nav.cities') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') }
  ];

  return (
    <header
      data-testid="navigation-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-obsidian/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="font-playfair text-xl md:text-2xl tracking-tight text-[#F5F5F0] hover:text-gold transition-colors duration-300"
          >
            VELMONT <span className="text-gold">SOCIETY</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.slice(0, -1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-xs uppercase tracking-[0.15em] font-manrope font-medium transition-colors duration-300 underline-hover ${
                  location.pathname === link.path
                    ? 'text-gold'
                    : 'text-[#A1A1AA] hover:text-[#F5F5F0]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Dropdown */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                data-testid="language-toggle"
                className="flex items-center gap-2 text-[#52525B] hover:text-gold transition-colors px-2 py-1"
              >
                <Globe size={14} />
                <span className="text-xs uppercase tracking-wide">{language}</span>
                <ChevronDown size={12} className={`transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-obsidian-paper border border-white/10 py-2 min-w-[140px] z-50">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLanguage(lang);
                        setIsLangMenuOpen(false);
                      }}
                      data-testid={`lang-option-${lang}`}
                      className={`w-full text-left px-4 py-2 text-sm font-manrope transition-colors ${
                        language === lang
                          ? 'text-gold bg-gold/10'
                          : 'text-[#A1A1AA] hover:text-[#F5F5F0] hover:bg-white/5'
                      }`}
                    >
                      {languageNames[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="relative group overflow-hidden border border-gold text-gold px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-500 hover:text-black"
            >
              <span className="relative z-10">{t('nav.contact')}</span>
              <div className="absolute inset-0 bg-gold transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-4 md:hidden">
            {/* Mobile Language Dropdown */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="text-[#52525B] hover:text-gold transition-colors flex items-center gap-1"
              >
                <Globe size={18} />
                <span className="text-xs uppercase">{language}</span>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-obsidian-paper border border-white/10 py-2 min-w-[140px] z-50">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setLanguage(lang);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm font-manrope transition-colors ${
                        language === lang
                          ? 'text-gold bg-gold/10'
                          : 'text-[#A1A1AA] hover:text-[#F5F5F0] hover:bg-white/5'
                      }`}
                    >
                      {languageNames[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-[#F5F5F0] hover:text-gold transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden fixed inset-0 top-20 bg-obsidian z-40"
        >
          <div className="flex flex-col items-center justify-center h-full gap-8">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-2xl font-playfair transition-colors duration-300 opacity-0 animate-fade-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className={location.pathname === link.path ? 'text-gold' : 'text-[#F5F5F0] hover:text-gold'}>
                  {link.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
